import Phaser from 'phaser'
import type { ActiveUnit, Element } from '@interfaces/index'
import { TEXTURE_KEYS } from '@interfaces/index'
import type { MatchEngine } from '@engine/MatchEngine'
import { cartToIso } from '@engine/Grid'
import type { GridRenderer } from './GridRenderer'
import { AssetRegistry } from '../AssetRegistry'

const ELEMENT_COLORS: Record<Element, number> = {
  fire:    0xff6633,
  water:   0x33aaff,
  nature:  0x44ee66,
  elektro: 0xffff33,
  light:   0xffffff,
  shadow:  0x9933ff,
  neutral: 0xaaaaaa,
}

/** Movement direction expressed as compass string for frame key naming. */
type UnitDir = 'e' | 'w' | 'n' | 's'

/**
 * Pooled sprite object for units that have an atlas.
 * Health bars are now drawn on the shared _healthBarGfx, so this no longer
 * carries its own Graphics object.
 */
interface UnitSpriteObject {
  sprite: Phaser.GameObjects.Sprite
  lastFrameKey: string
  inUse: boolean
}

/**
 * Lightweight per-unit label (Text) that is still per-object because
 * Phaser Text cannot be drawn onto a shared Graphics primitive.
 */
interface UnitLabel {
  label: Phaser.GameObjects.Text
  inUse: boolean
}

export class UnitRenderer {
  // ── Shared single-pass Graphics objects ──────────────────────────────────
  /** Draws ALL unit body circles in one clear() + N fill calls per frame. */
  private _bodyGfx: Phaser.GameObjects.Graphics
  /** Draws ALL health bars in one clear() + N fill calls per frame. */
  private _healthBarGfx: Phaser.GameObjects.Graphics

  // ── Pooled per-unit labels (Text cannot be batched into Graphics) ─────────
  private _labelPool: UnitLabel[] = []
  /** Maps unitId → label pool slot index for active units. */
  private _activeLabelSlots: Map<string, number> = new Map()

  // ── Pooled sprites for units with an atlas ────────────────────────────────
  private _spritePool: UnitSpriteObject[] = []
  /** Maps unitId → sprite pool slot index for active sprite units. */
  private _activeSpriteSlots: Map<string, number> = new Map()

  // ── Max pool sizes ────────────────────────────────────────────────────────
  private static readonly POOL_SIZE = 24

  private readonly scene: Phaser.Scene
  private readonly engine: MatchEngine
  private readonly playerId: string
  private readonly gridRenderer: GridRenderer
  /** Which player's field is currently being viewed (own or spy). */
  private viewPlayerId: string
  // Throttle the unit-count CustomEvent dispatch: only fire when counts change.
  private _lastEnemyCount = -1
  private _lastSentCount  = -1
  // Reused each frame to avoid GC pressure from repeated Set allocations.
  private _liveIds: Set<string> = new Set()
  // 'compact' drops shadow + inner-rim circles — saves ~2 draw calls/unit on low-end Android.
  private _renderQuality: 'full' | 'compact' = 'full'

  constructor(scene: Phaser.Scene, engine: MatchEngine, playerId: string, gridRenderer: GridRenderer) {
    this.scene = scene
    this.engine = engine
    this.playerId = playerId
    this.viewPlayerId = playerId
    this.gridRenderer = gridRenderer

    // Create the two shared Graphics objects once.
    this._bodyGfx      = scene.add.graphics().setDepth(8)
    this._healthBarGfx = scene.add.graphics().setDepth(9)

    // Pre-allocate label pool.
    for (let i = 0; i < UnitRenderer.POOL_SIZE; i++) {
      const label = scene.add.text(0, 0, '', {
        fontSize: '11px',
        color: '#ffffff',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 2,
      }).setOrigin(0.5, 0.5).setDepth(10).setVisible(false)
      this._labelPool.push({ label, inUse: false })
    }

    // Pre-allocate sprite pool (sprites will be configured per-unit on first use;
    // we create them without a valid texture now — they'll be set up on demand).
    // We defer sprite creation until we know the atlas key.
  }

  /**
   * Switch render quality. 'compact' skips shadow + inner-rim circles, saving
   * ~2 GPU draw calls per unit — use when FPS drops below 28 on low-end devices.
   */
  setQuality(quality: 'full' | 'compact'): void {
    this._renderQuality = quality
  }

  /** Called by MatchScene when spy mode toggles to switch the rendered unit set. */
  setViewPlayerId(id: string): void {
    if (this.viewPlayerId === id) return
    this.viewPlayerId = id
    // Return all active pool slots to idle so the next update() starts fresh.
    this._returnAllToPool()
    this._lastEnemyCount = -1
    this._lastSentCount  = -1
  }

  private _returnAllToPool(): void {
    // Return labels
    for (const [, slotIdx] of this._activeLabelSlots) {
      const slot = this._labelPool[slotIdx]
      if (slot !== undefined) {
        slot.label.setVisible(false).setText('')
        slot.inUse = false
      }
    }
    this._activeLabelSlots.clear()

    // Return sprites
    for (const [, slotIdx] of this._activeSpriteSlots) {
      const slot = this._spritePool[slotIdx]
      if (slot !== undefined) {
        slot.sprite.setVisible(false).setActive(false)
        slot.inUse = false
      }
    }
    this._activeSpriteSlots.clear()

    // Clear shared graphics (no live units)
    this._bodyGfx.clear()
    this._healthBarGfx.clear()
  }

  /** Return a single unit's label slot back to the pool. */
  private _releaseLabelFor(unitId: string): void {
    const slotIdx = this._activeLabelSlots.get(unitId)
    if (slotIdx === undefined) return
    const slot = this._labelPool[slotIdx]
    if (slot !== undefined) {
      slot.label.setVisible(false).setText('')
      slot.inUse = false
    }
    this._activeLabelSlots.delete(unitId)
  }

  /** Return a single unit's sprite slot back to the pool. */
  private _releaseSpriteFor(unitId: string): void {
    const slotIdx = this._activeSpriteSlots.get(unitId)
    if (slotIdx === undefined) return
    const slot = this._spritePool[slotIdx]
    if (slot !== undefined) {
      slot.sprite.setVisible(false).setActive(false)
      slot.inUse = false
    }
    this._activeSpriteSlots.delete(unitId)
  }

  /** Acquire a free label slot, expanding the pool if necessary (safety valve). */
  private _acquireLabel(unitId: string, text: string): Phaser.GameObjects.Text {
    // Check if already active
    const existing = this._activeLabelSlots.get(unitId)
    if (existing !== undefined) {
      return this._labelPool[existing]!.label
    }
    // Find a free slot
    let freeIdx = this._labelPool.findIndex(s => !s.inUse)
    if (freeIdx === -1) {
      // Pool exhausted — create a new slot (safety; should not happen under normal load)
      const label = this.scene.add.text(0, 0, '', {
        fontSize: '11px',
        color: '#ffffff',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 2,
      }).setOrigin(0.5, 0.5).setDepth(10)
      freeIdx = this._labelPool.length
      this._labelPool.push({ label, inUse: false })
    }
    const slot = this._labelPool[freeIdx]!
    slot.inUse = true
    slot.label.setText(text).setVisible(true)
    this._activeLabelSlots.set(unitId, freeIdx)
    return slot.label
  }

  /** Acquire a free sprite slot for a unit. Creates Phaser Sprite on demand. */
  private _acquireSprite(unitId: string, atlasKey: string, frameKey: string | null): UnitSpriteObject {
    // Check if already active
    const existingIdx = this._activeSpriteSlots.get(unitId)
    if (existingIdx !== undefined) {
      return this._spritePool[existingIdx]!
    }
    // Find a free slot
    let freeIdx = this._spritePool.findIndex(s => !s.inUse)
    if (freeIdx === -1) {
      // Create a new slot (safety valve; rare beyond 24 units)
      const sprite = this.scene.add.sprite(0, 0, atlasKey, frameKey ?? undefined)
      sprite.setDepth(8).setScale(0.40625).setActive(false).setVisible(false)
      freeIdx = this._spritePool.length
      this._spritePool.push({ sprite, lastFrameKey: '', inUse: false })
    }
    const slot = this._spritePool[freeIdx]!
    slot.inUse = true
    slot.sprite.setActive(true).setVisible(frameKey !== null)
    if (frameKey !== null) slot.sprite.setFrame(frameKey)
    slot.lastFrameKey = frameKey ?? ''
    this._activeSpriteSlots.set(unitId, freeIdx)
    return slot
  }

  update(): void {
    const { cellW, cellH, originX, originY } = this.gridRenderer
    const radius = Math.max(5, cellH * 0.38)

    // Collect only units that are on the currently viewed field.
    const allUnits: ActiveUnit[] = this.engine.getActiveUnits(this.viewPlayerId)

    // ── Release pool slots for dead/gone units ────────────────────────────────
    // Reuse the class-level Set to avoid per-frame allocation pressure on Android.
    this._liveIds.clear()
    for (const u of allUnits) { if (u.alive) this._liveIds.add(u.id) }

    for (const [id] of this._activeLabelSlots) {
      if (!this._liveIds.has(id)) this._releaseLabelFor(id)
    }
    for (const [id] of this._activeSpriteSlots) {
      if (!this._liveIds.has(id)) this._releaseSpriteFor(id)
    }

    // ── Single-pass shared Graphics clear ────────────────────────────────────
    this._bodyGfx.clear()
    this._healthBarGfx.clear()

    let enemyCount = 0
    let sentCount  = 0

    for (const unit of allUnits) {
      if (!unit.alive) continue   // D) Dead unit skip (already present, kept explicit)

      const isMine = unit.ownerId === this.playerId
      if (isMine) sentCount++; else enemyCount++

      // ── Sub-cell smooth interpolation ─────────────────────────────────────
      // Each unit carries its own per-unit path (unit.path).
      // pathIdx is a float: floor = current node, frac = progress toward next.
      let interpGx: number
      let interpGy: number
      const unitPath = unit.path
      if (unitPath.length >= 2) {
        const floorIdx = Math.min(Math.floor(unit.pathIdx), unitPath.length - 1)
        const frac     = unit.pathIdx - Math.floor(unit.pathIdx)
        const nodeA    = unitPath[floorIdx] ?? { gx: unit.gx, gy: unit.gy }
        const nodeB    = unitPath[Math.min(floorIdx + 1, unitPath.length - 1)] ?? nodeA
        interpGx = nodeA.gx + (nodeB.gx - nodeA.gx) * frac
        interpGy = nodeA.gy + (nodeB.gy - nodeA.gy) * frac
      } else {
        interpGx = unit.gx
        interpGy = unit.gy
      }

      const iso = cartToIso(interpGx, interpGy, cellW, cellH)
      // Own units: offset slightly above (negative Y) so they don't overlap enemy units
      const sx = iso.x + originX
      const sy = iso.y + originY + (isMine ? -(cellH * 0.6) : 0)

      // ── Derive movement direction for frame naming ────────────────────────
      const dir: UnitDir = 'e'

      // ── Atlas sprite path ─────────────────────────────────────────────────
      if (AssetRegistry.hasUnitAtlas(unit.id, this.scene)) {
        this._renderUnitSprite(unit, sx, sy, radius, isMine, dir, cellH)
        // Ensure any lingering label for this unit is hidden
        // (sprite path doesn't use labels)
        this._releaseLabelFor(unit.id)
        continue
      }

      // ── Procedural fallback — shared Graphics body ────────────────────────
      // Release any sprite slot this unit might have had (e.g. atlas unloaded)
      this._releaseSpriteFor(unit.id)

      const color = ELEMENT_COLORS[unit.el] ?? 0xaaaaaa
      const compact = this._renderQuality === 'compact'

      // A) All unit bodies drawn on the single shared _bodyGfx object.
      // Shadow circle — skipped in compact mode (saves 1 fill per unit).
      if (!compact) {
        this._bodyGfx.fillStyle(0x000000, 0.3)
        this._bodyGfx.fillCircle(sx, sy + 2, radius)
      }

      if (isMine) {
        // Own units: element fill, semi-transparent, blue outer ring
        this._bodyGfx.fillStyle(color, 0.82)
        this._bodyGfx.fillCircle(sx, sy, radius)
        // White inner rim — skipped in compact mode (saves 1 stroke per unit).
        if (!compact) {
          this._bodyGfx.lineStyle(1.5, 0xffffff, 0.5)
          this._bodyGfx.strokeCircle(sx, sy, radius * 0.65)
        }
        // Blue outer ring
        this._bodyGfx.lineStyle(2, 0x44aaff, 1.0)
        this._bodyGfx.strokeCircle(sx, sy, radius)
      } else {
        // Enemy units: solid fill, thin dark outline, red outer ring
        this._bodyGfx.fillStyle(color, 1.0)
        this._bodyGfx.fillCircle(sx, sy, radius)
        // White inner rim — skipped in compact mode.
        if (!compact) {
          this._bodyGfx.lineStyle(1, 0xffffff, 0.4)
          this._bodyGfx.strokeCircle(sx, sy, radius * 0.62)
        }
        // Red outer ring
        this._bodyGfx.lineStyle(2, 0xff2222, 1.0)
        this._bodyGfx.strokeCircle(sx, sy, radius)
      }

      // A) Health bars on the single shared _healthBarGfx object.
      if (!isMine) {
        this._drawHealthBar(this._healthBarGfx, unit, sx, sy, radius, cellH)
      }

      // Label: still per-unit Text object (cannot batch into Graphics)
      const label = this._acquireLabel(unit.id, unit.el.charAt(0).toUpperCase())
      label.setPosition(sx, sy)
    }

    if (enemyCount !== this._lastEnemyCount || sentCount !== this._lastSentCount) {
      this._lastEnemyCount = enemyCount
      this._lastSentCount  = sentCount
      window.dispatchEvent(new CustomEvent('ultimaze:unit-counts', {
        detail: { enemy: enemyCount, sent: sentCount }
      }))
    }
  }

  /**
   * Render a unit using its per-entity atlas sprite.
   * Health bar is drawn on the shared _healthBarGfx (not a per-sprite Graphics).
   */
  private _renderUnitSprite(
    unit: ActiveUnit,
    sx: number,
    sy: number,
    radius: number,
    isMine: boolean,
    dir: UnitDir,
    cellH: number,
  ): void {
    const atlasKey = TEXTURE_KEYS.UNIT_ATLAS(unit.id)
    const frameIndex = AssetRegistry.animFrame(6, 8) // 6 walk frames @ 8 fps
    const frameKey = AssetRegistry.getUnitFrameKeyWithFallback(this.scene, unit, 'walk', dir, frameIndex)

    const sprObj = this._acquireSprite(unit.id, atlasKey, frameKey)
    const sprite = sprObj.sprite

    sprite.setPosition(sx, sy)
    if (frameKey !== null) {
      if (sprObj.lastFrameKey !== frameKey) {
        sprite.setFrame(frameKey)
        sprObj.lastFrameKey = frameKey
      }
      sprite.setVisible(true)
    } else {
      sprite.setVisible(false)
    }

    // Flip sprite horizontally for west-moving units
    sprite.setFlipX(dir === 'w')

    // Tint own units slightly blue, enemies slightly red
    if (isMine) {
      sprite.setTint(0xaaddff)
    } else {
      sprite.clearTint()
    }

    // A) HP bar drawn on shared _healthBarGfx (no per-sprite Graphics object)
    if (!isMine) {
      this._drawHealthBar(this._healthBarGfx, unit, sx, sy, radius, cellH)
    }
  }

  private _drawHealthBar(
    gfx: Phaser.GameObjects.Graphics,
    unit: ActiveUnit,
    sx: number,
    sy: number,
    radius: number,
    cellH: number,
  ): void {
    const hpRatio = unit.maxHp > 0 ? unit.curHp / unit.maxHp : 0
    const barW = radius * 2.5
    const barH = Math.max(3, cellH * 0.15)
    const barY = sy - radius - barH - 3
    const barColor = hpRatio > 0.5 ? 0x00ee00 : hpRatio >= 0.25 ? 0xffaa00 : 0xff2200
    // Background track
    gfx.fillStyle(0x111111, 0.8)
    gfx.fillRect(sx - barW / 2, barY, barW, barH)
    // Fill
    gfx.fillStyle(barColor, 1.0)
    gfx.fillRect(sx - barW / 2, barY, Math.round(barW * hpRatio), barH)
    // Thin border
    gfx.lineStyle(0.5, 0x000000, 0.6)
    gfx.strokeRect(sx - barW / 2, barY, barW, barH)
  }

  clear(): void {
    this._returnAllToPool()
  }

  destroy(): void {
    this._returnAllToPool()
    this._bodyGfx.destroy()
    this._healthBarGfx.destroy()
    for (const slot of this._labelPool) slot.label.destroy()
    for (const slot of this._spritePool) slot.sprite.destroy()
    this._labelPool.length = 0
    this._spritePool.length = 0
  }
}
